import React, { useState, useRef, useMemo, useEffect } from 'react'
import debounce from 'lodash/debounce'
import Wait from '@react/react-spectrum/Wait'
import ComboBox from '@react/react-spectrum/ComboBox'
import Textfield from '@react/react-spectrum/Textfield'
import Star from '@react/react-spectrum/Icon/Star'
import { getIntegrationDetails } from '@demo-system/commons/utilities/integrations'

import { useDsnApiV2 } from 'services/axios'
import { useProject } from 'hooks'

import './DatastreamPicker.scss'

function DatastreamCombobox({ disabled, loading, handleChange, options, defaultValue }) {
  const [textfieldValue, setTextfieldValue] = useState(defaultValue)
  const [showMenu, setShowMenu] = useState(false)

  const renderItem = ({ label, isRecommended }) => {
    if (isRecommended) {
      return (
        <span>
          <Star size='XS' className='DatastreamPicker__Star' />
          {label}
        </span>
      )
    }
    return <span>{label}</span>
  }

  const handleBlur = () => {
    setShowMenu(false)
  }

  const handleSelect = (val) => {
    setShowMenu(false)
    setTextfieldValue(val.label)
    handleChange(val)
  }

  const _handleChange = (val) => {
    setTextfieldValue(val)
    setShowMenu(true)
  }

  const handleFocus = () => setShowMenu(true)

  return (
    <div>
      {loading ? (
        <Wait size='S' className='DatastreamPicker__Wait' />
      ) : (
        <ComboBox
          value={textfieldValue}
          options={options}
          renderItem={renderItem}
          disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          showMenu={showMenu}
          placeholder='Select datastream'
          onSelect={handleSelect}
          onChange={_handleChange}
          onMenuToggle={setShowMenu}
        />
      )}
    </div>
  )
}

function DatastreamFallbackInput({
  defaultValue,
  linkedName,
  onChange,
  disabled,
  integrationNotExisting,
  allowInputWithEmptySandbox,
}) {
  const [textfieldValue, setTextfieldValue] = useState(defaultValue)
  const handleChange = useRef(
    debounce((value) => {
      onChange({ id: value, title: linkedName })
    }, 1000)
  )

  const handleInputChange = (value) => {
    setTextfieldValue(value)
    handleChange.current(value)
  }

  const fallbackInfo = integrationNotExisting
    ? 'You must choose your IMS Organization and Sandbox before entering the datastream'
    : `It was not possible to fetch datastreams for your organization. ${
        allowInputWithEmptySandbox ? 'Make sure to provide the proper sandbox name. Otherwise, please' : 'Please'
      } enter the datastream ID manually.`

  return (
    <div>
      {/* We can't get the datastream's own title (that also needs the
          currently-blocked edge.adobe.io product) — this is the associated
          Launch property's name instead, shown so the raw ID below isn't
          the only clue as to what this datastream actually is. */}
      {linkedName && <div className='DatastreamPicker__LinkedName'>Linked via Launch property: {linkedName}</div>}
      <Textfield placeholder='Datastream ID' value={textfieldValue} disabled={disabled} onChange={handleInputChange} />
      <div className='DatastreamPicker__DatastreamFallbackInfo'>{fallbackInfo}</div>
    </div>
  )
}

export default function DatastreamPicker({ value, disabled, onChange, allowInputWithEmptySandbox }) {
  const { project } = useProject()
  const { orgId, sandboxName } = getIntegrationDetails(project?.integrations)

  const [{ data, loading, error }] = useDsnApiV2({
    url: '/aep/datastreams',
    method: 'GET',
    params: {
      orgId,
      sandboxName,
    },
  })
  const propertyName = project?.integrations?.launch?.propertyName
  const propertyId = project?.integrations?.launch?.propertyID

  // Fallback source for the datastream ID when /aep/datastreams (the org-wide
  // list, edge.adobe.io) errors — e.g. that API product isn't entitled for
  // this org even though everything else works. /launch reads the property's
  // own Web SDK extension settings via Reactor (a separate, already-working
  // Adobe API), which already contains the exact datastream ID this property
  // uses — no org-wide list or name-matching heuristic needed.
  const [{ data: launchData }, fetchLaunch] = useDsnApiV2(
    {
      url: '/launch',
      method: 'GET',
      params: { propertyId, imsOrgID: orgId },
    },
    { manual: true }
  )

  useEffect(() => {
    if (propertyId && orgId) fetchLaunch().catch(() => {})
  }, [propertyId, orgId, fetchLaunch])

  const launchEdgeConfigId = launchData?.edgeConfigId
  // Best available stand-in for the datastream's own name (which needs the
  // currently-blocked edge.adobe.io product) — the Launch property this
  // datastream is configured on, which is a reasonable proxy in practice
  // since it's almost always set up per-property.
  const launchPropertyLabel = launchData?.attributes?.name

  const [options, optionsMap, defaultValue] = useMemo(() => {
    const _optionsMap = new Map()
    let _options = []
    let firstDefaultValue
    let secondDefaultValue

    if (data?.result) {
      const propertyNamePartial = propertyName?.split('(')[0]?.trim()
      data.result.forEach((d) => {
        let isRecommended = false
        if (d.id === launchEdgeConfigId || d.title === propertyName) {
          firstDefaultValue = d
          isRecommended = true
        } else if (d.title.startsWith(propertyNamePartial)) {
          secondDefaultValue = d
          isRecommended = true
        }
        _optionsMap.set(d.id, d)
        _options.push({
          label: d.title,
          value: d.id,
          isRecommended,
        })
      })
    }

    _options.sort((a, b) => {
      if (a.value === firstDefaultValue?.id || (a.isRecommended && !b.isRecommended)) {
        return -1
      }
      if (b.value === firstDefaultValue?.id || (!a.isRecommended && b.isRecommended)) {
        return 1
      }
      return a.label.localeCompare(b.label)
    })

    return [_options, _optionsMap, firstDefaultValue || secondDefaultValue]
  }, [data, propertyName, launchEdgeConfigId])

  useEffect(() => {
    if (defaultValue && !value) {
      onChange(defaultValue)
    }
  }, [defaultValue, value, onChange])

  // The org-wide list failed, but we still have the exact ID from the
  // property's own extension settings — seed and auto-save that instead of
  // leaving the fallback field empty and requiring fully manual entry.
  useEffect(() => {
    if (error && launchEdgeConfigId && !value) {
      onChange({ id: launchEdgeConfigId, title: launchPropertyLabel })
    }
  }, [error, launchEdgeConfigId, launchPropertyLabel, value, onChange])

  const handleChange = (val) => onChange(optionsMap.get(val.value))
  const integrationNotExisting = allowInputWithEmptySandbox ? !orgId : !orgId || !sandboxName

  if (error || integrationNotExisting) {
    return (
      <DatastreamFallbackInput
        disabled={disabled || integrationNotExisting}
        defaultValue={value?.id || launchEdgeConfigId || ''}
        linkedName={value?.title || launchPropertyLabel}
        onChange={onChange}
        integrationNotExisting={integrationNotExisting}
        allowInputWithEmptySandbox
      />
    )
  }

  return (
    <DatastreamCombobox
      loading={loading}
      disabled={disabled}
      handleChange={handleChange}
      options={options}
      defaultValue={value?.title || defaultValue?.title || ''}
    />
  )
}
